// ./src/hooks/usePlaceAutocomplete.ts
import { useState, useCallback, useEffect, useRef } from "react";
import {
  useGoogleMapsAutocomplete,
  PlacePrediction,
  PlaceData,
} from "@/hooks/useGoogleMapsAutocomplete";
import { GeoCoords } from "@/hooks/useGeolocation";
import { debounce } from "@/utils/misc";

// ============================================
// Constants
// ============================================

const DEBOUNCE_INTERVAL_MS = 300;
const MIN_SEARCH_LENGTH = 2; // Lower threshold for place names (e.g., "BP")
const DEFAULT_RADIUS_METERS = 5000;

// ============================================
// Types
// ============================================

export interface UsePlaceAutocompleteOptions {
  /** Place types to search for (default: ['gas_station']) */
  includedTypes?: string[];
  /** Search radius in meters (default: 50000 = 50km) */
  radiusMeters?: number | undefined;
}

export interface UsePlaceAutocompleteReturn {
  /** List of place predictions from search */
  placePredictions: PlacePrediction[];
  /** Whether search is in progress */
  loading: boolean;
  /** Current search input value */
  searchInput: string;
  /** Fetch place predictions with optional location bias */
  fetchPlacePredictions: (
    query: string,
    locationBias?: GeoCoords | null
  ) => void;
  /** Fetch full place details by ID */
  fetchPlaceDetails: (placeId: string) => Promise<PlaceData | null>;
  /** Clear predictions and reset state */
  clearPredictions: () => void;
}

// Re-export types for convenience
export type {
  PlacePrediction,
  PlaceData,
} from "@/hooks/useGoogleMapsAutocomplete";

// ============================================
// Hook
// ============================================

/**
 * Place Autocomplete Hook
 * Provider-agnostic hook for searching establishments (e.g., gas stations)
 * Manages UI state, debouncing, session management, and service calls
 *
 * @example
 * ```tsx
 * const { placePredictions, loading, fetchPlacePredictions, fetchPlaceDetails } =
 *   usePlaceAutocomplete({ includedTypes: ['gas_station'] });
 *
 * // Search with location bias
 * fetchPlacePredictions("Shell", { latitude: 51.04, longitude: -114.07 });
 *
 * // Get full details when user selects
 * const placeData = await fetchPlaceDetails(prediction.placeId);
 * ```
 */
export const usePlaceAutocomplete = (
  options: UsePlaceAutocompleteOptions = {}
): UsePlaceAutocompleteReturn => {
  const {
    includedTypes = ["gas_station"],
    radiusMeters = DEFAULT_RADIUS_METERS,
  } = options;

  // State
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  // Service layer - Google Maps implementation
  const {
    isLoaded,
    createAutocompleteSession,
    searchPlaces,
    fetchPlaceDetails: fetchPlaceDetailsFromService,
  } = useGoogleMapsAutocomplete();

  // Session management (Google uses sessions for billing optimization)
  const sessionRef = useRef<any>(null);
  const debouncedFetchRef = useRef<ReturnType<typeof debounce> | null>(null);

  /**
   * Starts a new autocomplete session
   */
  const startNewSession = useCallback(async () => {
    sessionRef.current = await createAutocompleteSession();

    if (!sessionRef.current) {
      console.error("Failed to create new autocomplete session");
    }

    return sessionRef.current;
  }, [createAutocompleteSession]);

  /**
   * Initialize session when service loads
   */
  useEffect(() => {
    if (isLoaded && !sessionRef.current) {
      startNewSession();
    }
  }, [isLoaded, startNewSession]);

  /**
   * Core search logic
   */
  const executeFetch = useCallback(
    async (query: string, locationBias?: GeoCoords | null) => {
      if (!query || query.trim().length < MIN_SEARCH_LENGTH) {
        setPlacePredictions([]);
        setLoading(false);
        setSearchInput(query);
        return;
      }

      if (!isLoaded) {
        console.error("Google API Service not loaded yet");
        setLoading(false);
        return;
      }

      setLoading(true);
      setSearchInput(query);

      try {
        // Ensure we have a session
        if (!sessionRef.current) {
          await startNewSession();
        }

        const results = await searchPlaces(query, {
          sessionToken: sessionRef.current,
          locationBias,
          radiusMeters,
          includedTypes,
        });

        setPlacePredictions(results || []);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch place predictions:", error);
        setLoading(false);
        setPlacePredictions([]);
      }
    },
    [isLoaded, searchPlaces, startNewSession, radiusMeters, includedTypes]
  );

  /**
   * Keep a ref to the latest executeFetch so the debounced function
   * always calls the current version without needing to be recreated.
   */
  const executeFetchRef = useRef(executeFetch);
  useEffect(() => {
    executeFetchRef.current = executeFetch;
  }, [executeFetch]);

  /**
   * Create stable debounced function once on mount.
   * Delegates to executeFetchRef.current so it always
   * uses the latest closure without being recreated.
   */
  useEffect(() => {
    debouncedFetchRef.current = debounce(
      (q: string, loc?: GeoCoords | null) => {
        executeFetchRef.current(q, loc);
      },
      DEBOUNCE_INTERVAL_MS
    );

    return () => {
      if (debouncedFetchRef.current?.cancel) {
        debouncedFetchRef.current.cancel();
      }
    };
  }, []);

  /**
   * Debounced search to prevent excessive API calls.
   * Stable reference — never changes.
   */
  const fetchPlacePredictions = useCallback(
    (query: string, locationBias?: GeoCoords | null) => {
      debouncedFetchRef.current?.(query, locationBias);
    },
    []
  );

  /**
   * Fetches full details when user selects a place
   *
   * @param placeId - Google Place ID
   * @returns Full place data including name and address
   */
  const fetchPlaceDetails = useCallback(
    async (placeId: string): Promise<PlaceData | null> => {
      if (!placeId) {
        console.error("No placeId provided to fetchPlaceDetails");
        return null;
      }

      try {
        // Ensure we have a session
        if (!sessionRef.current) {
          await startNewSession();
        }

        const currentSession = sessionRef.current;

        // Start new session for next search cycle (completes billing session)
        await startNewSession();

        // Fetch details using the session that was used for search
        const placeData = await fetchPlaceDetailsFromService(
          placeId,
          currentSession
        );

        return placeData;
      } catch (error) {
        console.error("Failed to fetch place details:", error);
        return null;
      }
    },
    [fetchPlaceDetailsFromService, startNewSession]
  );

  /**
   * Clears search state
   */
  const clearPredictions = useCallback(() => {
    setPlacePredictions([]);
    setSearchInput("");
    setLoading(false);
  }, []);

  return {
    placePredictions,
    loading,
    searchInput,
    fetchPlacePredictions,
    fetchPlaceDetails,
    clearPredictions,
  };
};